import { normalizeResidency } from "./taxEngine";
import { MONTHS } from "../constants";

export function buildPayslipHTML(employee, monthKey, data, ytdGross, ytdTax) {
  const { rateLines, allowances, result } = data;
  const [year, monthNum] = monthKey.split("-");
  const monthName = MONTHS[parseInt(monthNum) - 1];
  const f = (n, d = 2) => Number(n || 0).toLocaleString("en-PG", { minimumFractionDigits: d, maximumFractionDigits: d });
  const p = (n) => (Number(n || 0) * 100).toFixed(2) + "%";

  const validLines = rateLines.filter(l => parseFloat(l.rate) > 0 && (l.rateMode === "monthly" || parseFloat(l.days) > 0));
  const validAllowances = allowances.filter(a => parseFloat(a.amount) > 0);

  const currency = result.currency || "PGK";
  const fxRate   = result.fxRate  || 1;
  const isFx     = currency !== "PGK";

  const earningRows = validLines.map(l => {
    const isMonthly = l.rateMode === "monthly";
    const rateNative = parseFloat(l.rate);
    const ratePGK    = isFx ? rateNative * fxRate : rateNative;
    const totalNative = isMonthly ? rateNative : rateNative * parseFloat(l.days);
    const totalPGK    = isMonthly ? ratePGK : ratePGK * parseFloat(l.days);
    return `
    <tr>
      <td>${l.label}</td>
      <td class="muted">
        ${isMonthly
          ? `Monthly rate: ${currency} ${f(rateNative)}`
          : `${l.days} days &times; ${currency} ${f(rateNative)}/day`}
        ${isFx ? `<span style="color:#bbb">&nbsp;(@ ${f(fxRate,4)} PGK/${currency})</span>` : ""}
      </td>
      <td class="right mono">${isFx ? `<div style="font-size:9.5px;color:#999">${currency} ${f(totalNative)}</div>` : ""}PGK ${f(totalPGK)}</td>
      <td></td>
    </tr>`;
  }).join("");

  const allowanceRows = validAllowances.map(a => {
    const amtNative = parseFloat(a.amount) || 0;
    const amtPGK = isFx ? amtNative * fxRate : amtNative;
    const total = a.type === "per_diem" ? amtPGK * result.totalDays : amtPGK;
    return `
    <tr>
      <td>${a.label || (a.type === "expense" ? "Expense" : "Allowance")}</td>
      <td class="muted">${a.type === "per_diem"
        ? `${currency} ${f(amtNative)} &times; ${result.totalDays} days${isFx ? ` <span style="color:#bbb">(@ ${f(fxRate,4)} PGK/${currency})</span>` : ""}`
        : a.type === "expense" ? "Expense reimbursement" : "Monthly fixed"}</td>
      <td></td>
      <td class="right mono">${isFx ? `<div style="font-size:9.5px;color:#999">${currency} ${f(a.type === "per_diem" ? amtNative * result.totalDays : amtNative)}</div>` : ""}PGK ${f(total)}</td>
    </tr>`;
  }).join("");

  const newYTDGross = ytdGross + result.monthlyGross;
  const newYTDTax = ytdTax + result.monthlyTax;

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>payslip_${(employee.name || "Employee").replace(/\s+/g, "_")}_${monthName}_${year}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:'Inter',Arial,sans-serif;font-size:11.5px;color:#212737;background:#fff}
  .page{width:210mm;min-height:297mm;margin:0 auto}

  /* HEADER */
  .hdr{background:#31b4a6;color:#fff;padding:20px 30px 16px;display:flex;justify-content:space-between;align-items:flex-end}
  .co-name{font-size:28px;font-weight:700;letter-spacing:-0.5px}
  .co-sub{font-size:9px;opacity:.75;margin-top:3px;letter-spacing:.1em;text-transform:uppercase}
  .ps-title{font-size:20px;font-weight:600;text-align:right}
  .ps-period{font-size:11px;opacity:.85;margin-top:2px;text-align:right}

  /* INFO BAND */
  .info{background:#f8fafd;border-bottom:2px solid #31b4a6;padding:14px 30px;display:grid;grid-template-columns:1fr 1fr;gap:20px}
  .lbl{font-size:8.5px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:#8a8a9a;margin-bottom:2px}
  .val{font-size:13px;font-weight:600;color:#212737}
  .val-sm{font-size:11px;color:#384460;margin-top:1px}
  .badge{display:inline-block;padding:2px 7px;border-radius:3px;font-size:8px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;margin-left:6px;vertical-align:middle}
  .resident{background:#ddffee;color:#226644}
  .non-resident{background:#ddeeff;color:#3366aa}

  /* BODY */
  .body{padding:18px 30px}
  .sec-title{font-size:8.5px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#31b4a6;border-bottom:1px solid #e0e0e0;padding-bottom:4px;margin:16px 0 8px}

  table{width:100%;border-collapse:collapse}
  th{font-size:8.5px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:#8a8a9a;padding:5px 8px;text-align:left;background:#f8fafd}
  td{padding:6px 8px;border-bottom:1px solid #f2f2f2;color:#212737;vertical-align:top}
  tr:last-child td{border-bottom:none}
  .right{text-align:right}
  .mono{font-family:'Courier New',monospace;font-size:11px}
  .muted{color:#666;font-size:10px}

  .total-row td{font-weight:700;font-size:12px;background:#f0faf9;border-top:2px solid #31b4a6;border-bottom:none;color:#212737}
  .total-row .right{color:#31b4a6}
  .tax-row td{font-weight:700;font-size:12px;background:#fff4ee;border-top:2px solid #f68644;border-bottom:none}
  .tax-row .right{color:#f68644}

  /* SUMMARY */
  .summary{margin-top:16px;border:2px solid #31b4a6;border-radius:5px;overflow:hidden}
  .summary-hdr{background:#31b4a6;color:#fff;padding:7px 16px;font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase}
  .summary-grid{display:grid;grid-template-columns:repeat(3,1fr)}
  .sc{padding:10px 14px;border-right:1px solid #e0f0ef}
  .sc:last-child{border-right:none}
  .sc .l{font-size:8px;color:#8a8a9a;letter-spacing:.08em;text-transform:uppercase;font-weight:600;margin-bottom:3px}
  .sc .v{font-size:15px;font-weight:700;font-family:'Courier New',monospace}
  .teal{color:#31b4a6}.orange{color:#f68644}.gold{color:#c29a52}

  /* YTD */
  .ytd{margin-top:12px;background:#f8fafd;border:1px solid #e0e0e0;border-radius:5px;padding:10px 16px;display:flex;gap:40px}
  .ytd-item .l{font-size:8px;color:#8a8a9a;letter-spacing:.08em;text-transform:uppercase;font-weight:600;margin-bottom:2px}
  .ytd-item .v{font-size:13px;font-weight:700;font-family:'Courier New',monospace;color:#212737}

  /* FOOTER */
  .footer{margin-top:24px;padding:12px 30px;border-top:1px solid #e0e0e0;display:flex;justify-content:space-between;align-items:center}
  .footer-txt{font-size:8px;color:#aaa;line-height:1.6}
  .sig{width:150px;border-top:1px solid #ccc;text-align:center;padding-top:3px;font-size:8px;color:#aaa}

  @media print{
    body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .page{margin:0;width:100%}
    @page{margin:0;size:A4 portrait}
  }
</style>
</head>
<body>
<div class="page">

<div class="hdr">
  <div>
    <div class="co-name">easy skill</div>
    <div class="co-sub">Papua New Guinea &middot; Payroll Department</div>
  </div>
  <div>
    <div class="ps-title">Pay Slip</div>
    <div class="ps-period">${monthName} ${year}</div>
  </div>
</div>

<div class="info">
  <div>
    <div class="lbl">Employee</div>
    <div class="val">${employee.name || "&mdash;"}
      <span class="badge ${normalizeResidency(employee.empType) === "resident" ? "resident" : "non-resident"}">
        ${normalizeResidency(employee.empType) === "resident" ? "Resident" : "Non-Resident"}
      </span>
    </div>
    ${employee.matricule ? `<div class="val-sm">${employee.matricule}</div>` : ""}
    ${employee.position ? `<div class="val-sm">${employee.position}</div>` : ""}
    ${employee.address ? `<div style="margin-top:6px"><div class="lbl">Address</div><div class="val-sm">${employee.address}</div></div>` : ""}
    <div style="margin-top:6px"><div class="val-sm">DEP ${Math.min(parseInt(employee.dependants || 0), 3)}</div></div>
  </div>
  <div style="text-align:right">
    <div class="lbl">Pay Period</div>
    <div class="val">${monthName} ${year}</div>
    <div style="margin-top:8px"><div class="lbl">Project</div><div class="val-sm">${employee.project || "&mdash;"}</div></div>
    <div style="margin-top:8px"><div class="lbl">Payment Frequency</div><div class="val-sm">Monthly</div></div>
  </div>
</div>

<div class="body">

  <div class="sec-title">Taxable Earnings &mdash; Daily Rates</div>
  <table>
    <thead><tr><th>Rate Type</th><th>Detail</th><th class="right">Taxable Net (PGK)</th><th></th></tr></thead>
    <tbody>
      ${earningRows}
      <tr class="total-row">
        <td colspan="2">Total Net Taxable Earnings</td>
        <td class="right mono">
          ${isFx ? `<div style="font-size:10px;color:#666;font-weight:400">${currency} ${f(result.fxNet || result.totalNet / fxRate)}</div>` : ""}
          PGK ${f(result.totalNet)}
        </td>
        <td></td>
      </tr>
    </tbody>
  </table>

  ${validAllowances.length > 0 ? `
  <div class="sec-title">Allowances (Tax Exempt)</div>
  <table>
    <thead><tr><th>Description</th><th>Basis</th><th></th><th class="right">Amount (PGK)</th></tr></thead>
    <tbody>
      ${allowanceRows}
      <tr class="total-row">
        <td colspan="3">Total Allowances</td>
        <td class="right mono">PGK ${f(result.totalAllowances)}</td>
      </tr>
    </tbody>
  </table>` : ""}

  <div class="sec-title">Salary Tax Calculation (PNG Salary &amp; Wages Tax)</div>
  ${result.taxExempt ? `
  <div style="background:#fff8f0;border:2px solid #f68644;border-radius:6px;padding:12px 18px;margin-bottom:10px;text-align:center">
    <div style="color:#f68644;font-weight:700;font-size:13px;letter-spacing:0.1em;text-transform:uppercase">Tax Exempt</div>
    <div style="color:#999;font-size:9px;margin-top:3px">Internationally-funded project — exempt from PNG Salary &amp; Wages Tax</div>
  </div>
  <table>
    <thead><tr><th>Description</th><th class="right">Amount (PGK)</th></tr></thead>
    <tbody>
      <tr>
        <td>Gross Salary (this month)</td>
        <td class="right mono muted">${f(result.monthlyGross)}</td>
      </tr>
      <tr>
        <td>Effective Tax Rate</td>
        <td class="right" style="color:#f68644;font-weight:700">0.00% (Exempt)</td>
      </tr>
      <tr class="tax-row">
        <td>Salary Tax Withheld (this month)</td>
        <td class="right mono">PGK 0.00</td>
      </tr>
    </tbody>
  </table>` : `
  <table>
    <thead><tr><th>Description</th><th class="right">Amount (PGK)</th></tr></thead>
    <tbody>
      <tr>
        <td>Gross Salary (this month)</td>
        <td class="right mono muted">${f(result.monthlyGross)}</td>
      </tr>
      <tr>
        <td>Effective Tax Rate</td>
        <td class="right">${p(result.effectiveRate)}</td>
      </tr>
      <tr class="tax-row">
        <td>Salary Tax Withheld (this month)</td>
        <td class="right mono">PGK ${f(result.monthlyTax)}</td>
      </tr>
    </tbody>
  </table>`}

  <div class="summary">
    <div class="summary-hdr">Pay Summary &mdash; ${monthName} ${year}</div>
    <div class="summary-grid">
      <div class="sc"><div class="l">Net Earned (Taxable)</div><div class="v teal">PGK ${f(result.totalNet)}</div></div>
      <div class="sc"><div class="l">Tax Withheld</div><div class="v orange">PGK ${f(result.monthlyTax)}</div></div>
      <div class="sc"><div class="l">Gross Salary</div><div class="v">PGK ${f(result.monthlyGross)}</div></div>
    </div>
    ${result.fxNet != null ? `
    <div style="background:#fffdf5;border-top:1px solid #f0e8d0;padding:8px 14px;display:flex;align-items:center;gap:24px">
      <div style="font-size:8px;color:#8a8a9a;letter-spacing:.08em;text-transform:uppercase;font-weight:600">Amount Paid to Employee</div>
      <div style="font-size:16px;font-weight:700;font-family:'Courier New',monospace;color:#c29a52">${result.currency} ${f(result.fxNet)}</div>
      <div style="font-size:10px;color:#aaa">(= PGK ${f(result.totalNet)} @ ${f(result.fxRate || 1, 4)} PGK/${result.currency || ""})</div>
    </div>` : ""}
    ${result.totalAllowances > 0 ? `
    <div style="border-top:1px solid #e0f0ef">
    <div class="summary-grid">
      <div class="sc"><div class="l">Allowances (Exempt)</div><div class="v" style="font-size:13px">PGK ${f(result.totalAllowances)}</div></div>
      <div class="sc"><div class="l">Total Paid to Employee</div><div class="v gold" style="font-size:14px">PGK ${f(result.totalNet + result.totalAllowances)}</div></div>
      <div class="sc"></div>
    </div>
    </div>` : ""}
  </div>

  <div class="sec-title">Year-to-Date &mdash; ${year}</div>
  <div class="ytd">
    <div class="ytd-item"><div class="l">YTD Gross Salary</div><div class="v">PGK ${f(newYTDGross)}</div></div>
    <div class="ytd-item"><div class="l">YTD Tax Withheld</div><div class="v">PGK ${f(newYTDTax)}</div></div>
    <div class="ytd-item"><div class="l">YTD Net Paid</div><div class="v">PGK ${f(newYTDGross - newYTDTax)}</div></div>
  </div>

</div>

<div class="footer">
  <div class="footer-txt">
    This is a computer-generated payslip and does not require a signature.<br/>
    Easy Skill &middot; www.easy-skill.com &middot; Australia &middot; France &middot; New Caledonia &middot; Papua New Guinea &middot; Mozambique
  </div>
  <div class="sig">Authorised Signatory</div>
</div>

</div>
</body>
</html>`;
}
